import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Root as RadixDropdownMenu,
  Trigger,
  Content,
  Portal,
  Item,
} from '@radix-ui/react-dropdown-menu'
import { Box } from '@moonpig/launchpad-components'
import { styled, useTheme } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import { useIsMobile } from '@moonpig/web-shared-utils'
import { DropDownButton } from '../DropDownButton'
import { ResultsButton } from '../../FiltersMenu/components'
import { useSearchStore } from '../../../store/SearchStore'
import { useDrawer } from '../../../contexts/drawer'
import { useFindLocaleText } from '../../../text-localisation'
import {
  buildRecipientOptions,
  groupRecipientsAlphabetically,
  RecipientOption,
} from './buildRecipientOptions'
import { RecipientList } from './RecipientList'
import { RecipientSignInPrompt } from './RecipientSignInPrompt'
import { useAutoOpenRecipients } from './useAutoOpenRecipients'

const MENU_OFFSET_PX = '144px'
const MENU_MIN_WIDTH_PX = '360px'
const MENU_MAX_HEIGHT_PX = '480px'
const MENU_MIN_HEIGHT_PX = '136px'

const StyledDropdownContent = styled(Content)`
  position: relative;
  overflow-y: auto;
  overscroll-behavior: contain;
  max-height: min(calc(100vh - ${MENU_OFFSET_PX}), ${MENU_MAX_HEIGHT_PX});
  min-height: ${MENU_MIN_HEIGHT_PX};
  z-index: 1050;
  ${s({
    bgcolor: 'colorBackground01',
    minWidth: MENU_MIN_WIDTH_PX,
    boxShadow: 4,
    borderRadius: 4,
    borderColor: 'colorBorder03',
    border: 2,
  })};
`

const StyledFooterContainer = styled(Box)`
  justify-content: center;
  ${s({
    bgcolor: 'colorBackground01',
    borderTop: 1,
    borderColor: 'colorBlack20',
    position: 'sticky',
    bottom: 0,
  })}
`

export const RecipientsDropDown: FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedRecipientId, setSelectedRecipientId] = useState<string | null>(
    null,
  )
  const dropdownButtonRef = useRef<HTMLButtonElement>(null)
  const isMobile = useIsMobile()
  const theme = useTheme()
  const localiseText = useFindLocaleText()
  const {
    show: showDrawer,
    update: updateDrawerContent,
    close: closeDrawer,
  } = useDrawer()

  const recipients = useSearchStore(store => store.recipientsWithReminders)
  const isAuthenticated = useSearchStore(store => store.isAuthenticated)
  const recipientOptions = useMemo(
    () => buildRecipientOptions(recipients),
    [recipients],
  )
  const groupedRecipients = useMemo(
    () => groupRecipientsAlphabetically(recipientOptions),
    [recipientOptions],
  )

  const label = localiseText('find.recipients_filter')
  const applyLabel = localiseText('find.recipients_filter.apply')

  const selectedCount =
    selectedRecipientId &&
    recipientOptions.some(
      (recipient: RecipientOption) => recipient.id === selectedRecipientId,
    )
      ? 1
      : 0

  const handleClose = useCallback(() => {
    setIsOpen(false)
    closeDrawer()
  }, [closeDrawer])

  const handleApply = useCallback(() => {
    handleClose()
  }, [handleClose])

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
  }

  const applyButton = (
    <ResultsButton
      onClick={handleApply}
      loading={false}
      resultsCount={0}
      label={applyLabel}
      compact={!isMobile}
    />
  )

  const menuBody = !isAuthenticated ? (
    <RecipientSignInPrompt inDropdownMenu={!isMobile} />
  ) : (
    <RecipientList
      groupedRecipients={groupedRecipients}
      selectedRecipientId={selectedRecipientId}
      onSelect={setSelectedRecipientId}
      inDropdownMenu={!isMobile}
    />
  )

  const menuFooter = isAuthenticated ? (
    <StyledFooterContainer data-testid="recipients-filter-apply-footer">
      {isMobile ? (
        applyButton
      ) : (
        <Item data-testid="recipients-filter-apply-item" onSelect={handleApply}>
          {applyButton}
        </Item>
      )}
    </StyledFooterContainer>
  ) : undefined

  const menuHeader = {
    left: 'close-button' as const,
    right: <Box width="48px" aria-hidden />,
  }

  const drawerBody = [<Box key="recipients-filter-list-body">{menuBody}</Box>]

  const openMobileDrawer = () => {
    setIsOpen(true)
    showDrawer({
      title: label,
      triggerRef: dropdownButtonRef,
      header: menuHeader,
      footer: menuFooter,
      body: drawerBody,
      onClose: () => setIsOpen(false),
    })
  }

  useEffect(() => {
    if (isOpen && isMobile) {
      updateDrawerContent(drawerBody, menuFooter, menuHeader)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isMobile, isAuthenticated, selectedRecipientId, recipientOptions])

  useAutoOpenRecipients({
    triggerRef: dropdownButtonRef,
    mdBreakpointPx: theme.breakpoints.map.md,
    onOpenDesktop: () => setIsOpen(true),
    onOpenMobile: openMobileDrawer,
  })

  if (isMobile) {
    return (
      <DropDownButton
        ref={dropdownButtonRef}
        label={label}
        isOpen={isOpen}
        count={selectedCount}
        onClick={openMobileDrawer}
      />
    )
  }

  return (
    <RadixDropdownMenu
      open={isOpen}
      onOpenChange={handleOpenChange}
      modal={false}
    >
      <Trigger asChild>
        <DropDownButton
          ref={dropdownButtonRef}
          label={label}
          isOpen={isOpen}
          count={selectedCount}
          onClick={() => {}}
        />
      </Trigger>
      <Portal>
        <StyledDropdownContent
          align="start"
          sideOffset={8}
          side="bottom"
          avoidCollisions={false}
        >
          {menuBody}
          {menuFooter}
        </StyledDropdownContent>
      </Portal>
    </RadixDropdownMenu>
  )
}
